import { yupResolver } from '@hookform/resolvers/yup';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { putOrderTpSlApi } from '@flipster/apis/v2/trade';
import { useHandleSubmit } from '@flipster/features/src/shared/react-hook-form/use-handle-submit';
import { TpSlTriggerTimeIndicatorMapper } from '@flipster/features/src/tpsl-modal/constant';
import { useDeleteTpSlLegacy } from '@flipster/features/src/tpsl-modal/use-delete-tp-sl';
import { ordersBunja } from '@flipster/r230522/original/order';
import { getTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { dateToEpochString } from '@flipster/utils/datetime';
import Num from '@flipster/utils/num';

import { helperBunja } from '@web/features/modals/TimeTriggerTpslModal/bunja/helper';
import SymbolInfo from '@web/features/modals/TimeTriggerTpslModal/components/SymbolInfo';
import { useTimeTriggerTpslBaseInfo } from '@web/features/modals/TimeTriggerTpslModal/useTimeTriggerTpslBaseInfo';
import { useTimeTriggerTpslModal } from '@web/features/modals/TimeTriggerTpslModal/useTimeTriggerTpslModal';
import {
  pendingTimeTriggerTpslConfirmClickEvent,
  pendingTimeTriggerTpslDeleteConfirmClickEvent,
} from '@web/utils/trade-event';

import { TpslTimeTriggerSchema, validationBunja } from './bunja/validation';
import ConfirmButton from './components/trigger-time/ConfirmButton';
import TriggerPriceRatioInput from './components/trigger-time/TriggerPriceRatioInput';
import TriggerPriceRatioRate from './components/trigger-time/TriggerPriceRatioRate';
import TriggerTimeInfos from './components/trigger-time/TriggerTimeInfos';

const percentPrecision = 2;

const TriggerTimePosition = () => {
  const transPosition = useTranslations('Position.r250401');
  const {
    deleteTpSlAlert,
    tpSlErrorHandler,
    adjustTriggerTimeTpSlRatioForUserInput,
    adjustTriggerTimeTpSlPercentForServer,
    getTriggerTimeDirection,
  } = useBunja(helperBunja);
  const { tpslTimeTriggerValidator } = useBunja(validationBunja);
  const methods = useForm<TpslTimeTriggerSchema>({
    mode: 'onChange',
    resolver: yupResolver(tpslTimeTriggerValidator),
  });

  const { control, handleSubmit, reset, setValue } = methods;

  const { pendingPositionsAtom } = useBunja(ordersBunja);
  const positions = useAtomValue(pendingPositionsAtom);
  const { tpSlInfo, onCloseTimeTriggerTpslModal } = useTimeTriggerTpslModal();
  const [, deleteTpSl] = useDeleteTpSlLegacy();
  const { symbol, socketSymbol, apiSymbol } = useTimeTriggerTpslBaseInfo();
  const currentTriggerPriceRatio = useWatch({ control, name: 'triggerPriceRatio' });

  const currentPosition = positions?.[symbol]?.find((p) => p.slot === tpSlInfo?.slot);
  const pricePrecision = apiSymbol?.pricePrecision || 2;
  const maintMarginRate = apiSymbol?.maintMarginRate;
  const side = currentPosition?.side;
  const leverage = currentPosition?.initLeverage;
  const referencePrice = getTickerDisplayPriceOrDefault(socketSymbol);

  const estPnlPercent = currentTriggerPriceRatio
    ? Num(currentTriggerPriceRatio)
        .multipliedBy(leverage ?? 1)
        .multipliedBy(tpSlInfo?.tpSlType === 'tp' ? 1 : -1)
        .toString()
    : undefined;
  const prevTriggerPriceRatio = useMemo(() => {
    if (!tpSlInfo?.tpSlType || !currentPosition) return '';
    return adjustTriggerTimeTpSlRatioForUserInput(
      side,
      tpSlInfo.tpSlType,
      currentPosition[tpSlInfo.tpSlType === 'tp' ? 'orderTpTriggerPriceRatio' : 'orderSlTriggerPriceRatio'] as string,
    );
  }, [currentPosition, side, tpSlInfo, adjustTriggerTimeTpSlRatioForUserInput]);
  const isExistPrevTriggerPriceRatio = Number(prevTriggerPriceRatio) > 0;
  const disabled = Boolean(
    (!prevTriggerPriceRatio && currentTriggerPriceRatio && Num(currentTriggerPriceRatio).isZero()) ||
      (currentTriggerPriceRatio && Num(currentTriggerPriceRatio).isEqualTo(prevTriggerPriceRatio)),
  ); // 새로 tp/sl 설정 시 0 입력 or 기존 값과 동일하게 입력 시 disabled

  const [isLoading, onSubmit] = useHandleSubmit(handleSubmit, async ({ triggerPriceRatio }) => {
    if (!tpSlInfo || !side) return;

    if (!triggerPriceRatio || Num(triggerPriceRatio).isZero()) {
      if (isExistPrevTriggerPriceRatio) {
        deleteTpSlAlert(tpSlInfo, () => {
          deleteTpSl(tpSlInfo.side, tpSlInfo.tpSlType, tpSlInfo.symbol, tpSlInfo.slot);
          pendingTimeTriggerTpslDeleteConfirmClickEvent({
            symbol,
            side,
            tpSlType: tpSlInfo.tpSlType,
            triggerTimestamp: currentPosition?.triggerTimestamp ?? '',
            triggerPriceRatio: `${TpSlTriggerTimeIndicatorMapper[side][tpSlInfo.tpSlType]}${triggerPriceRatio}`,
            amount: currentPosition.amount,
            qty: currentPosition.qty,
          });
          onCloseTimeTriggerTpslModal();
        });
        return;
      }
      onCloseTimeTriggerTpslModal();
      return;
    }

    const upsertTpSl = async () => {
      try {
        await putOrderTpSlApi(symbol, currentPosition.orderId, {
          requestId: uuid(),
          timestamp: dateToEpochString(new Date()),
          type: tpSlInfo.tpSlType === 'tp' ? 'OrderTp' : 'OrderSl',
          trigger: {
            triggerPriceRatio: adjustTriggerTimeTpSlPercentForServer(side, tpSlInfo.tpSlType, triggerPriceRatio),
            triggerDirection: getTriggerTimeDirection(side, tpSlInfo.tpSlType),
          },
        });
        snackbar.success({ message: transPosition('Position_update_toast') });
        pendingTimeTriggerTpslConfirmClickEvent({
          symbol,
          side,
          initLeverage: currentPosition.initLeverage ?? 1,
          tpSlType: tpSlInfo.tpSlType,
          triggerTimestamp: currentPosition.triggerTimestamp ?? '',
          amount: currentPosition.amount,
          qty: currentPosition.qty,
          currentPriceRatio: `${TpSlTriggerTimeIndicatorMapper[side][tpSlInfo.tpSlType]}${triggerPriceRatio}`,
          estimatedPnl: estPnlPercent ?? '',
        });
        onCloseTimeTriggerTpslModal();
      } catch (e) {
        tpSlErrorHandler(e, onCloseTimeTriggerTpslModal);
      }
    };
    await upsertTpSl();
  });

  useEffect(() => {
    if (tpSlInfo) {
      reset({
        side,
        positionType: tpSlInfo.positionType,
        tpSlType: tpSlInfo.tpSlType,
        maintMarginRate,
        leverage,
      });

      setValue('triggerPriceRatio', prevTriggerPriceRatio, {
        shouldDirty: true,
      });
    } else {
      reset();
    }
  }, [isExistPrevTriggerPriceRatio, leverage, maintMarginRate, prevTriggerPriceRatio, reset, setValue, side, tpSlInfo]);

  useEffect(() => {
    if (!currentPosition) {
      onCloseTimeTriggerTpslModal();
    }
  }, [currentPosition, onCloseTimeTriggerTpslModal]);

  if (!tpSlInfo || !currentPosition?.triggerTimestamp) return null;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <SymbolInfo
          symbol={tpSlInfo.symbol}
          displayPrice={referencePrice}
          pricePrecision={pricePrecision}
          side={currentPosition.side}
          leverage={currentPosition.initLeverage}
        />
        <TriggerPriceRatioInput precision={percentPrecision} side={side} tpSlType={tpSlInfo.tpSlType} />
        <TriggerTimeInfos triggerTime={currentPosition.triggerTimestamp} estPnlPercent={estPnlPercent} />
        <TriggerPriceRatioRate side={currentPosition.side} tpSlType={tpSlInfo.tpSlType} precision={percentPrecision} />
        <ConfirmButton isLoading={isLoading} disabled={disabled} />
      </form>
    </FormProvider>
  );
};

export default TriggerTimePosition;
